import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Search } from "lucide-react";
import { fetchPosts, subscribe, CATEGORIES, CATEGORY_COLOR } from "@/lib/blogApi";
import BlogCard from "@/components/blog/BlogCard";

const TABS = ["All", ...CATEGORIES];
const PAGE_SIZE = 6;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPosts({ category: tab, search: search || undefined })
      .then((p) => { if (alive) setPosts(p); })
      .catch(() => toast.error("Could not load blog posts"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tab, search]);

  useEffect(() => { setPage(1); }, [tab, search]);

  // SEO: inject JSON-LD (Blog + ItemList) into document head
  useEffect(() => {
    if (!posts.length) return;
    const itemList = posts.slice(0, 20).map((p, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "url": `${window.location.origin}/journal/${p.slug}`,
      "name": p.title,
    }));
    const ld = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Spiruva Journal",
      "description": "Application strategy, purity benchmarks, formulation thinking, and pre-launch insights for the global phycocyanin and spirulina platform.",
      "url": `${window.location.origin}/journal`,
      "publisher": {
        "@type": "Organization",
        "name": "Spiruva",
        "url": window.location.origin,
      },
      "blogPost": posts.slice(0, 10).map((p) => ({
        "@type": "BlogPosting",
        "headline": p.title,
        "url": `${window.location.origin}/journal/${p.slug}`,
        "datePublished": p.publishedAt,
        "author": { "@type": "Person", "name": p.author?.name },
        "articleSection": p.category,
      })),
      "mainEntity": { "@type": "ItemList", "itemListElement": itemList },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-blog-index-ld", "1");
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [posts]);

  const featured = useMemo(() => posts.find((p) => p.isFeatured) || posts[0], [posts]);
  const rest = useMemo(() => posts.filter((p) => p !== featured), [posts, featured]);

  const totalPages = Math.max(1, Math.ceil(rest.length / PAGE_SIZE));
  const visible = rest.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const categoryCounts = useMemo(() => {
    const map = {};
    posts.forEach((p) => { map[p.category] = (map[p.category] || 0) + 1; });
    return map;
  }, [posts]);

  const tagCloud = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 18);
  }, [posts]);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await subscribe(email, name || undefined);
      toast.success("You're on the list. Watch for market intelligence drops.");
      setEmail(""); setName("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally { setSubscribing(false); }
  };

  return (
    <div data-testid="page-blog" className="pt-[72px]">
      {/* HERO */}
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-green/40 via-brand-teal/20 to-transparent" />
        <div className="container-wide py-16 md:py-24 relative">
          <p className="eyebrow-light mb-5">◦  Spiruva Journal · Pre-Launch Insights</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">
            Industry Intelligence. <span className="italic text-brand-gold">Straight from the Source.</span>
          </h1>
          <p className="mt-5 md:mt-6 text-brand-cream/75 max-w-2xl leading-relaxed text-sm md:text-base">
            A science-led editorial hub covering application strategy, purity benchmarks, formulation thinking, export readiness, and advanced phycocyanin use cases ahead of the July 2027 launch window.
          </p>
        </div>
      </section>

      <section className="container-wide py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* MAIN */}
          <div className="lg:col-span-8 xl:col-span-9">
            {/* Featured */}
            {featured && !loading && (
              <div className="mb-10" data-testid="blog-featured-wrap">
                <BlogCard post={featured} featured />
              </div>
            )}

            {/* Search + chip filters */}
            <div className="flex flex-col gap-5 mb-8">
              <div className="relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-black/40" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  data-testid="blog-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm"
                />
              </div>
              <div data-testid="blog-filter-chips" className="flex flex-wrap gap-2">
                {TABS.map((t) => {
                  const c = CATEGORY_COLOR[t];
                  const active = tab === t;
                  const count = t === "All" ? posts.length : (categoryCounts[t] || 0);
                  const style = active && c
                    ? { backgroundColor: c.hex, color: c.text, borderColor: c.hex }
                    : active
                      ? { backgroundColor: "#0f1f14", color: "#f8f5ef", borderColor: "#0f1f14" }
                      : c
                        ? { color: c.hex, borderColor: `${c.hex}55`, backgroundColor: `${c.hex}10` }
                        : {};
                  return (
                    <button
                      key={t}
                      data-testid={`blog-chip-${t.replace(/\s+/g, '-')}`}
                      onClick={() => setTab(t)}
                      style={style}
                      className="group inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-mono uppercase tracking-[0.18em] transition hover:-translate-y-0.5"
                    >
                      <span>{t}</span>
                      {count > 0 && (
                        <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-white/60"}`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Grid */}
            {loading ? (
              <p className="text-brand-black/60 py-10" data-testid="blog-loading">Loading articles…</p>
            ) : visible.length === 0 ? (
              <p className="text-brand-black/60 py-10" data-testid="blog-empty">No articles match your filter.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {visible.map((p) => <BlogCard key={p.id} post={p} />)}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-brand-green/10" data-testid="blog-pagination">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-xs uppercase tracking-widest disabled:opacity-30 text-brand-black/70 hover:text-brand-gold"
                  data-testid="blog-prev"
                >
                  ← Previous
                </button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      data-testid={`blog-page-${i + 1}`}
                      className={`w-8 h-8 font-mono text-xs ${page === i + 1 ? "bg-brand-green text-brand-cream" : "text-brand-black/60 hover:text-brand-black"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="text-xs uppercase tracking-widest disabled:opacity-30 text-brand-black/70 hover:text-brand-gold"
                  data-testid="blog-next"
                >
                  Next →
                </button>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 xl:col-span-3 space-y-6">
            <div className="bg-white border border-brand-green/15 p-6">
              <p className="eyebrow mb-2">◦  Subscribe</p>
              <h3 className="font-display text-xl text-brand-black leading-snug">Get phycocyanin market intelligence in your inbox.</h3>
              <form onSubmit={onSubscribe} className="mt-4 space-y-2.5" data-testid="blog-subscribe-form">
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="subscribe-name"
                  className="w-full px-3 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm"
                />
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="subscribe-email"
                  className="w-full px-3 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="subscribe-submit"
                  className="w-full bg-brand-green text-brand-cream py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-brand-black transition disabled:opacity-60"
                >
                  {subscribing ? "Submitting…" : "Subscribe"}
                </button>
              </form>
            </div>

            <div className="bg-white border border-brand-green/15 p-6">
              <p className="eyebrow mb-3">◦  Categories</p>
              <ul className="space-y-2 text-sm">
                {CATEGORIES.map((c) => (
                  <li key={c}>
                    <button
                      onClick={() => setTab(c)}
                      className={`flex justify-between w-full text-left transition ${tab === c ? "text-brand-teal font-medium" : "text-brand-black/70 hover:text-brand-black"}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2" style={{ backgroundColor: CATEGORY_COLOR[c]?.hex }} />
                        {c}
                      </span>
                      <span className="font-mono text-[11px] text-brand-black/45">{categoryCounts[c] || 0}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {tagCloud.length > 0 && (
              <div className="bg-white border border-brand-green/15 p-6">
                <p className="eyebrow mb-3">◦  Popular Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {tagCloud.map((t) => (
                    <button
                      key={t}
                      onClick={() => setSearch(t)}
                      className="text-[11px] px-2.5 py-1 bg-brand-teal/8 border border-brand-teal/25 text-brand-teal hover:bg-brand-teal hover:text-brand-cream transition"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-brand-green text-brand-cream p-6 relative overflow-hidden">
              <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
              <div className="relative">
                <p className="eyebrow-light mb-2">◦  Pre-Launch Planning</p>
                <h3 className="font-display text-2xl leading-tight">Discuss 2027 supply planning</h3>
                <p className="text-sm text-brand-cream/80 mt-3 leading-relaxed">Reserve an allocation, request a technical review, or explore distribution.</p>
                <Link to="/reserve-allocation" className="btn-ghost-light mt-5 text-xs !py-3 !px-5" data-testid="blog-sidebar-cta">
                  Reserve Allocation <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* PROMINENT NEWSLETTER BAND — full-width, below grid */}
      <section
        data-testid="blog-newsletter-band"
        className="bg-brand-black text-brand-cream relative overflow-hidden border-t border-brand-green/20"
      >
        <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(circle at 20% 50%, rgba(0,109,119,0.6) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(201,168,76,0.25) 0%, transparent 50%)" }}
        />
        <div className="container-wide py-20 md:py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow-light mb-4">◦  Spiruva Intelligence Drop</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
                Get phycocyanin market signals <span className="italic text-brand-gold">before your competitors do.</span>
              </h2>
              <p className="text-brand-cream/75 mt-6 max-w-xl leading-relaxed">
                One curated email per month. Regulatory shifts, price benchmarks, export data, and procurement opportunities — straight to your inbox.
              </p>
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.18em] text-brand-cream/65">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-brand-gold" /> No spam</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-brand-gold" /> B2B only</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-brand-gold" /> Unsubscribe anytime</li>
              </ul>
            </div>
            <div className="lg:col-span-5">
              <form
                onSubmit={onSubscribe}
                data-testid="blog-newsletter-band-form"
                className="bg-brand-cream/5 backdrop-blur-sm border border-brand-cream/15 p-6 md:p-8 space-y-3"
              >
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="newsletter-band-name"
                  className="w-full px-4 py-3 bg-brand-black/40 border border-brand-cream/15 focus:border-brand-gold focus:outline-none text-sm text-brand-cream placeholder-brand-cream/40"
                />
                <input
                  type="email"
                  required
                  placeholder="your.work@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-band-email"
                  className="w-full px-4 py-3 bg-brand-black/40 border border-brand-cream/15 focus:border-brand-gold focus:outline-none text-sm text-brand-cream placeholder-brand-cream/40"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="newsletter-band-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black py-3.5 text-xs font-medium uppercase tracking-[0.22em] hover:bg-brand-cream transition disabled:opacity-60"
                >
                  {subscribing ? "Submitting…" : (
                    <>Subscribe Free <ArrowUpRight size={14} /></>
                  )}
                </button>
                <p className="text-[10px] font-mono uppercase tracking-widest text-brand-cream/45 pt-1">
                  Joined by buyers at 40+ brands across 12 countries
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
